import { gql } from '@apollo/client';

export const GET_MY_PROJECT_ACTIVITY_SUMMARY = gql`
  query getMyProjectActivitySummary($from: DateTime!, $to: DateTime!) {
    myProjectActivitySummary(from: $from, to: $to) {
      totalContributions
      activeDays
      currentStreak
      longestStreak
      availableYears
      trackingStartedAt
      days {
        date
        count
      }
    }
  }
`;

export const GET_MY_PROJECT_ACTIVITY_FEED = gql`
  query getMyProjectActivityFeed(
    $from: DateTime!
    $to: DateTime!
    $pagination: PaginationInput!
    $sort: ProjectActivitySortInput
  ) {
    myProjectActivityFeed(
      from: $from
      to: $to
      pagination: $pagination
      sort: $sort
    ) {
      items {
        id
        occurredAt
        project {
          id
          name
        }
        version {
          id
          number
          commitMessage
          createdFileCount
          updatedFileCount
          deletedFileCount
        }
      }
      pagination {
        totalItems
        totalPages
        currentPage
        pageSize
      }
    }
  }
`;

// Project Queries - Updated for new version control schema
export const GET_PROJECTS = gql`
  query getProjects {
    projects {
      _id
      name
      versionCount
      ownerUser {
        _id
        firstName
        lastName
      }
      description
      updatedAt
      members {
        artistName
      }
      versionHistory {
        _id
        version
      }
      previewFile {
        _id
        waveForm {
          duration
          peakCount
          peaks
        }
      }
    }
  }
`;

// Version-scoped preview, separate from the latest preview on library cards.
export const GET_PROJECT_PREVIEW = gql`
  query getProjectPreview($projectId: String!, $version: Float!) {
    projectVersion(projectId: $projectId, version: $version) {
      _id
      previewFile {
        _id
        fileName
        waveForm {
          duration
          peakCount
          peaks
        }
      }
    }
  }
`;

// Latest-version manifest for library filters; deliberately excludes waveforms.
export const GET_PROJECT_LIBRARY_FILES = gql`
  query getProjectLibraryFiles($projectId: String!, $version: Float!) {
    projectVersion(projectId: $projectId, version: $version) {
      _id
      files {
        _id
        fileName
        quickHash
        fullHash
        uploadedAt
        size
        contentType
        s3Key
      }
    }
  }
`;

export const GET_PROJECT = gql`
  query getProject($projectId: String!) {
    project(projectId: $projectId) {
      _id
      name
      description
      tags
      owner
      createdAt
      updatedAt
      versionHistory {
        _id
        version
        commitMessage
        deletedFiles
        createdFiles
        updatedFiles
        createdAt
        plugins {
          name
          vendor
          pluginType
          vst2Id
          confidence
          detectionMethod
          detectionCount
        }
      }
      members {
        _id
        userId
        userEmail
        artistName
        role
        joinedAt
      }
    }
  }
`;

export const GET_PROJECT_VERSION = gql`
  query getProjectVersion($projectId: String!, $version: Float!) {
    projectVersion(projectId: $projectId, version: $version) {
      _id
      name
      description
      tags
      owner
      createdAt
      updatedAt
      files {
        _id
        fileName
        contentType
        size
        uploadedAt
        s3Key
        quickHash
        fullHash
        replacesFileId
        waveForm {
          duration
          peakCount
          peaks
        }
      }
      versionHistory {
        _id
        version
        commitMessage
        deletedFiles
        createdFiles
        updatedFiles
        createdAt
        plugins {
          name
          vendor
          pluginType
          vst2Id
          confidence
          detectionMethod
          detectionCount
        }
      }
      members {
        _id
        userId
        userEmail
        role
        joinedAt
      }
    }
  }
`;

// Project Sharing Queries
export const GET_PROJECT_INVITES = gql`
  query getProjectInvites($projectId: String!) {
    projectInvitesSent(projectId: $projectId) {
      _id
      email
      status
      role
      createdAt
      expiresAt
    }
  }
`;

export const GET_MY_INVITES = gql`
  query getMyInvites {
    projectInvites {
      _id
      projectName
      invitedByEmail
      role
      expiresAt
    }
  }
`;

// Project Mutations
export const CREATE_PROJECT = gql`
  mutation createProject($data: ProjectCreateInput!) {
    projectCreate(data: $data) {
      _id
      name
      description
      tags
      createdAt
      versionHistory {
        _id
        version
        commitMessage
        deletedFiles
        createdFiles
        updatedFiles
        createdAt
      }
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation updateProject($projectId: String!, $data: ProjectUpdateInput!) {
    projectUpdate(projectId: $projectId, data: $data) {
      _id
      name
      description
      tags
      updatedAt
    }
  }
`;

export const DELETE_PROJECT = gql`
  mutation deleteProject($projectId: String!) {
    projectDelete(projectId: $projectId)
  }
`;

export const PROJECT_VERSION_PUSH = gql`
  mutation projectVersionPush(
    $projectId: String!
    $data: ProjectVersionPushInput!
  ) {
    projectVersionPush(projectId: $projectId, data: $data) {
      version {
        _id
      }
      uploadUrls {
        presignedUrl
        uploadRequired
        key
        quickHash
        fullHash
        replacesFileId
      }
    }
  }
`;

export const PROJECT_VERSION_ROLLBACK = gql`
  mutation projectVersionRollback($projectId: String!, $versionId: String!) {
    projectVersionRollback(projectId: $projectId, versionId: $versionId)
  }
`;

export const PROJECT_VERSION_FINALIZE = gql`
  mutation projectVersionFinalize($projectId: String!, $versionId: String!) {
    projectVersionFinalize(projectId: $projectId, versionId: $versionId)
  }
`;

// Project Sharing Mutations
export const INVITE_TO_PROJECT = gql`
  mutation inviteToProject($data: ProjectInviteInput!) {
    projectInvite(data: $data) {
      _id
      email
      projectName
      invitedByEmail
      role
      status
      expiresAt
    }
  }
`;

export const REMOVE_PROJECT_MEMBER = gql`
  mutation removeProjectMember($data: ProjectMemberRemoveInput!) {
    projectMemberRemove(data: $data)
  }
`;

export const LEAVE_PROJECT = gql`
  mutation leaveProject($projectId: String!) {
    projectLeave(projectId: $projectId)
  }
`;

export const RESPOND_TO_INVITE = gql`
  mutation respondToInvite($data: ProjectInviteResponseInput!) {
    projectInviteRespond(data: $data)
  }
`;

export const PROJECT_INVITE_CANCEL = gql`
  mutation projectInviteCancel($data: ProjectInviteCancelInput!) {
    projectInviteCancel(data: $data)
  }
`;

// File Mutations - Updated for new schema
export const FILE_UPLOAD = gql`
  mutation fileUpload($data: FileUploadInput!) {
    fileUpload(data: $data) {
      key
      presignedUrl
      quickHash
      fullHash
      replacesFileId
    }
  }
`;

export const FILE_DOWNLOAD = gql`
  mutation fileDownload($data: FileDownloadInput!) {
    fileDownload(data: $data) {
      presignedUrl
      fileName
      contentType
    }
  }
`;

export const FILE_DELETE = gql`
  mutation fileDelete($data: FileDeleteInput!) {
    fileDelete(data: $data)
  }
`;

export const GET_RENDER_FOLDER_OPTIONS = gql`
  query getRenderFolderOptions {
    renderFolderOptions
  }
`;
